// The coach: talk to it, see where you stand, and steer. A new reply first, if there is one.
// Then the week's line, the conversation (the live coach answers in seconds, with buttons that
// change the plan when tapped), fitness and form to Day 1, Paris as if it were tomorrow, the
// direction for the next block, the whole ramp (drag a week to propose a change), and notes.
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { haptic } from "../lib/haptic";
import { useMusette, useSync, setDirection, askCoach, withdrawAsk, applyAction, intervalsFit } from "../lib/store";
import { todayIso, dateLabel, shortDate, mondayOf } from "../lib/dates";
import { readout, parisToday, directions } from "../lib/analysis";
import { coachLine } from "../lib/coach";
import { fitness, fitnessLine } from "../lib/fitness";
import { Head } from "../components/Head";
import { Sheet } from "../components/Sheet";
import { FitChart } from "../components/FitChart";
import { useToast } from "../components/Sheet";
import type { Ask } from "../lib/types";

// The cloud coach runs at 20:45 every day and at 15:45 on Fridays for the weekly review (London time).
function nextRun(now = new Date()) {
  const t = now.toLocaleTimeString("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit", hour12: false });
  const fri = now.toLocaleDateString("en-GB", { timeZone: "Europe/London", weekday: "long" }) === "Friday";
  if (fri && t < "15:45") return "15:45";
  return t < "20:45" ? "20:45" : "20:45 tomorrow";
}
const SEEN = "musette.coach.seen";
const readSeen = () => { try { return localStorage.getItem(SEEN) ?? ""; } catch { return ""; } };
const writeSeen = (v: string) => { try { localStorage.setItem(SEEN, v); } catch { /* fine */ } };

const hm = (iso: string) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" });
const finePointer = () => typeof matchMedia === "function" && matchMedia("(hover: hover) and (pointer: fine)").matches;

const dayKey = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { timeZone: "Europe/London", weekday: "long", day: "numeric", month: "long" });
const OPENED = new Date().toISOString(); // replies newer than this arrive with a little motion

/** The conversation as messages: yours on the right in ink, the coach's on the left, one bubble
 *  per paragraph as if typed, its buttons as black blocks under the last one. */
function Thread({ asks, onDo, thinking }: { asks: Ask[]; onDo: (a: Ask, i: number) => void; thinking: string | false | undefined }) {
  let day = "";
  return (
    <div class="chat">
      {asks.map((a) => {
        const d = dayKey(a.at);
        const sep = d !== day ? (day = d, <div class="chat-day"><span>{d}</span></div>) : null;
        const paras = (a.reply ?? "").split(/\n\s*\n/).map((x) => x.trim()).filter(Boolean);
        const arrive = !!a.replyAt && a.replyAt > OPENED;
        return (
          <>
            {sep}
            {a.coach
              ? <div class="chat-group them"><span class="chat-who"><span class="coach-mark" aria-hidden="true">m</span>Coach</span><div class="bubble">{a.text}</div></div>
              : <div class="chat-group me">
                  <div class="bubble">{a.text}</div>
                  <span class="chat-meta">{hm(a.at)}{!a.reply && <> · <button type="button" class="link" onClick={() => withdrawAsk(a.at)}>Take back</button></>}</span>
                </div>}
            {a.reply ? (
              <div class={`chat-group them${arrive ? " arrive" : ""}`}>
                <span class="chat-who"><span class="coach-mark" aria-hidden="true">m</span>Coach{a.by === "cloud" ? "" : ", live"}</span>
                {paras.map((p, k) => <div class="bubble" style={`--k:${k}`}>{p}</div>)}
                {a.actions?.length ? (
                  <div class="chat-actions" style={`--k:${paras.length}`}>
                    {a.actions.map((x, i) => a.done?.includes(i)
                      ? <span class="chat-done"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7" /></svg>{x.label}</span>
                      : <button type="button" class="chat-act" onClick={() => onDo(a, i)}><span>{x.label}</span><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg></button>)}
                  </div>
                ) : null}
                {a.held?.length ? a.held.map((h) => <span class="soft held-line">Held back: {h.label}. {h.rule}</span>) : null}
                <span class="chat-meta">{hm(a.replyAt ?? a.at)}{a.handoff ? " · the deeper coach follows up" : ""}</span>
              </div>
            ) : (
              <div class="chat-group them">
                {thinking === a.at
                  ? <div class="bubble typing-b" aria-label="The coach is answering"><span class="typing"><i /><i /><i /></span></div>
                  : <span class="chat-meta">The cloud coach answers at {nextRun()}</span>}
              </div>
            )}
          </>
        );
      })}
    </div>
  );
}

/** The field you ask in. Grows to six lines; on a keyboard, Enter sends and Shift+Enter breaks the line. */
function Composer({ text, setText, onSend, busy }: { text: string; setText: (v: string) => void; onSend: () => void; busy: boolean }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { const el = ref.current; if (!el) return; el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, 22 * 6 + 10)}px`; }, [text]);
  return (
    <div class="composer">
      <textarea ref={ref} rows={1} placeholder="Ask in a sentence" value={text} aria-label="Ask the coach"
        onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.isComposing && finePointer()) { e.preventDefault(); if (!busy) onSend(); } }} />
      <button type="button" class="send" disabled={!text.trim() || busy} onClick={onSend} aria-label="Send">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5M6 11l6-6 6 6" /></svg>
      </button>
    </div>
  );
}

const QUICK = [
  "I'm away this week",
  "I'm not well, lighten the week",
  "Can I swap a morning for an evening?",
  "Make next week harder",
  "Rework the plan",
];

export function Coach() {
  const s = useMusette();
  const t = todayIso();
  const r = readout(s, t);
  const paris = parisToday(s);
  const dirs = directions(s);
  const chosen = s.coach?.direction?.id;
  const asks = s.log?.asks ?? [];
  const { show, node: toast } = useToast();
  const [text, setText] = useState("");
  const [convo, setConvo] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (convo) requestAnimationFrame(() => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; }); }, [convo, asks.length, asks.map((a) => a.replyAt ?? "").join()]);
  const [openDay, setOpenDay] = useState<number | null>(1);
  const monday = mondayOf(t);
  const iNow = Math.max(0, s.ramp.findIndex((w) => w.w === monday));
  const [pick, setPick] = useState(iNow);
  const maxKm = Math.max(r.seven.km, ...s.ramp.map((w) => w.km ?? 0)) * 1.05 || 1;
  const pw = s.ramp[pick];

  const sync = useSync();
  const TAB = "musette.coach.tab";
  const [tab, setTab] = useState<"fitness" | "paris" | "plan" | "notes">(() => { try { return (localStorage.getItem(TAB) as any) || "fitness"; } catch { return "fitness"; } });
  const pickTab = (k: typeof tab) => { if (k !== tab) haptic(); setTab(k); try { localStorage.setItem(TAB, k); } catch { /* fine */ } };
  const [icu, setIcu] = useState<import("../lib/fitness").Icu | null>(null);
  useEffect(() => { intervalsFit().then(setIcu); }, []);
  const f = useMemo(() => fitness(s, t, icu), [s.rides, s.plan, s.ramp, icu]);
  const [seen] = useState(readSeen());
  const fresh = [...asks].reverse().find((a) => a.reply && (a.replyAt ?? a.at) > seen && a.by === "cloud");
  useEffect(() => { const last = asks.reduce((m, a) => ((a.replyAt ?? "") > m ? a.replyAt! : m), seen); if (last > seen) writeSeen(last); }, [asks.length, asks.map((a) => a.replyAt).join()]);

  const send = async (v = text) => {
    v = v.trim();
    if (!v) return;
    setText("");
    const how = await askCoach(v);
    if (how === "queued") show(`Saved. The cloud coach answers at ${nextRun()}.`);
    if (how === "offline") show("No signal. Try again when you have one.");
  };
  const act = async (a: Ask, i: number) => show(await applyAction(a.at, i));

  // Drag a week of the ramp up or down to propose a different target; the coach decides.
  const rampRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ i: number; km: number; y0: number } | null>(null);
  const [propose, setPropose] = useState<{ i: number; km: number } | null>(null);
  const kmAt = (clientY: number) => { const r = rampRef.current!.getBoundingClientRect(); return Math.max(0, Math.round(((1 - (clientY - r.top) / r.height) * maxKm) / 5) * 5); };

  const quick = (
    <div class="chips quick">
      {QUICK.map((q) => <button type="button" class="chip" onClick={() => setText(q)}><span>{q}</span></button>)}
    </div>
  );

  return (
    <main class="screen signal">
      <Head right={`${r.weeks} weeks to Paris`} />

      <section class="stack" style="margin-top: var(--space-5)">
        {fresh && (
          <div class="card coach-card new">
            <span class="label amber">New from the coach</span>
            <Thread asks={[fresh]} onDo={act} thinking={false} />
          </div>
        )}
        <div class="card coach-card">
          <span class="label amber">From the coach · Week of {shortDate(monday)}</span>
          <span class="say big">{coachLine(s, t)}</span>
          <span class="soft">{r.words}</span>
        </div>

        <div class="card ask-card">
          <button type="button" class="row-between chat-open" onClick={() => setConvo(true)}>
            <span class="chat-title"><span class="coach-mark big" aria-hidden="true">m</span><span><span class="note-title">The coach</span><span class="label">{sync.thinking ? "Typing" : "Answers in seconds"}</span></span></span>
            <span class="label ink">{asks.length ? `All ${asks.length}` : "Open"} →</span>
          </button>
          {asks.length > 0 && <div class="chat-peek" onClick={(e) => { if (!(e.target as Element).closest("button")) setConvo(true); }}><Thread asks={asks.slice(-1)} onDo={act} thinking={sync.thinking} /></div>}
          {quick}
          <Composer text={text} setText={setText} onSend={() => { send(); setConvo(true); }} busy={!!sync.thinking} />
        </div>

        <Sheet open={convo} onClose={() => setConvo(false)} label="Conversation with the coach" tall>
          <div class="convo">
            <div class="convo-head"><span class="chat-title"><span class="coach-mark big" aria-hidden="true">m</span><span><span class="note-title">The coach</span><span class="label">{sync.thinking ? "Typing" : "Answers in seconds"}</span></span></span></div>
            <div class="convo-list" ref={listRef} aria-live="polite">
              <Thread asks={asks} onDo={act} thinking={sync.thinking} />
            </div>
            <div class="convo-foot">
              {quick}
              <Composer text={text} setText={setText} onSend={() => send()} busy={!!sync.thinking} />
            </div>
            {toast}
          </div>
        </Sheet>

        <div class="segmented four coach-tabs" role="tablist" aria-label="More from the coach">
          {(["fitness", "paris", "plan", "notes"] as const).map((k) => <button type="button" role="tab" aria-selected={tab === k} onClick={() => pickTab(k)}>{k === "fitness" ? "Fitness" : k === "paris" ? "Paris" : k === "plan" ? "Plan" : "Notes"}</button>)}
        </div>

        {tab === "fitness" && <div class="card">
          <div class="row-between"><span class="label ink">Fitness and form</span><span class="label">To 12 June</span></div>
          <FitChart f={f} today={t} />
          <span class="soft">{fitnessLine(f)}</span>
          <span class="label">{icu ? "Measured by intervals.icu" : "From Strava's Relative Effort"}. Solid is measured; dashed is an estimate from the plan.</span>
        </div>}

        {tab === "paris" && <div class="card">
          <div class="row-between"><span class="label ink">If Paris were tomorrow</span><span class="label">Longest {r.L?.km ?? 0} km</span></div>
          <div class="stage-list">
            {paris.map((d) => {
              const open = openDay === d.day;
              return (
                <button type="button" class={`stage${open ? " open" : ""}`} aria-expanded={open} onClick={() => setOpenDay(open ? null : d.day)}>
                  <div class="row-between"><span class="stage-name"><span class="label">Day {d.day}</span>{d.to}</span><span class="label">{d.km} km</span></div>
                  <div class="track thin"><span class="track-fill" style={`width:${Math.min(100, d.distPct)}%`} /></div>
                  {open && (
                    <div class="stage-more">
                      <div class="row-between"><span class="label">Climb</span><span class="label">{d.climbPct}% of {d.gain.toLocaleString("en-GB")} m</span></div>
                      <div class="track thin"><span class="track-fill" style={`width:${Math.min(100, d.climbPct)}%`} /></div>
                      <span class="soft">{d.verdict}. {d.distPct}% of the distance, {d.climbPct}% of the climbing.</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>}

        {tab === "plan" && <div class="card">
          <div class="row-between"><span class="label ink">Point the next block</span><span class="label">Pick one</span></div>
          <div class="choices" role="radiogroup" aria-label="Direction for the next block">
            {dirs.map((d) => (
              <button type="button" role="radio" aria-checked={chosen === d.id} class={`choice${chosen === d.id ? " on" : ""}`}
                onClick={() => { setDirection(d.id); show(`${d.title}. The cloud coach reworks the plan around it on Friday.`); }}>
                <span class="choice-dot" aria-hidden="true" />
                <span class="choice-text"><span class="choice-title">{d.title}</span><span class="soft">{d.line}</span><span class="label">{d.gives}</span></span>
              </button>
            ))}
          </div>
        </div>}

        {tab === "plan" && <div class="card">
          <div class="row-between"><span class="label ink">The ramp to Paris</span><span class="label">{s.ramp.length} weeks</span></div>
          <div class="ramp-all" role="radiogroup" aria-label="Weeks of the plan. Drag a week up or down to propose a change." ref={rampRef}
            onPointerDown={(e) => { const b = (e.target as HTMLElement).closest("button"); if (!b) return; const i = Number(b.dataset.i); setDrag({ i, km: s.ramp[i]?.km ?? 0, y0: e.clientY }); (e.currentTarget as Element).setPointerCapture(e.pointerId); }}
            onPointerMove={(e) => { if (drag && Math.abs(e.clientY - drag.y0) > 8) setDrag({ ...drag, km: kmAt(e.clientY) }); }}
            onPointerUp={(e) => { if (drag && Math.abs(e.clientY - drag.y0) > 8 && drag.km !== (s.ramp[drag.i]?.km ?? 0)) setPropose({ i: drag.i, km: drag.km }); else if (drag) setPick(drag.i); setDrag(null); }}>
            <span class="best" style={`bottom:${(r.seven.km / maxKm) * 100}%`} aria-hidden="true" />
            {s.ramp.map((wk, i) => {
              const km = wk.km ?? (wk.rides ? wk.rides * 18 : 0);
              const easy = /Easier/.test(wk.note ?? "");
              const shown = drag?.i === i ? drag.km : propose?.i === i ? propose.km : km;
              return <button type="button" data-i={i} role="radio" aria-checked={i === pick} aria-label={`Week of ${shortDate(wk.w)}`} class={`${i === iNow ? "now" : ""}${i === pick ? " on" : ""}${easy ? " easy" : ""}${i < iNow ? " past" : ""}${drag?.i === i || propose?.i === i ? " drafting" : ""}`} style={`--h:${Math.max(4, (shown / maxKm) * 100)}%`} onKeyDown={(e) => { if (e.key === "Enter") setPick(i); }}><span /></button>;
            })}
          </div>
          <div class="row-between"><span class="label">{shortDate(s.ramp[0]?.w ?? t)}</span><span class="label">Paris</span></div>
          {drag && <span class="label amber">Week of {shortDate(s.ramp[drag.i].w)}: {drag.km} km</span>}
          {propose && !drag && (
            <div class="row-between" style="gap: var(--space-2)">
              <button type="button" class="pill ghost small" onClick={() => setPropose(null)}>Keep it</button>
              <button type="button" class="pill small" onClick={() => { const w = s.ramp[propose.i]; setPropose(null); send(`Change the week of ${shortDate(w.w)} from ${w.km ?? `${w.rides} rides`}${w.km ? " km" : ""} to ${propose.km} km. Say what it does to the ramp.`); }}>Ask the coach: {propose.km} km</button>
            </div>
          )}
          {pw && !drag && !propose && <span class="soft"><b>{pick === iNow ? "This week" : `Week ${pick + 1}, ${shortDate(pw.w)}`}:</b> {pw.rides ? `${pw.rides} ${pw.rides === 1 ? "ride" : "rides"}` : `${pw.km} km`}{pw.note ? `. ${pw.note}` : ""}. The dashed line is your best seven days, {r.seven.km} km. Drag a week to propose a change.</span>}
        </div>}

        {tab === "notes" && <div class="card">
          <span class="label ink">Going well</span>
          {r.well.map((n) => <Note n={n} />)}
        </div>}
        {tab === "notes" && <div class="card">
          <span class="label ink">To work on</span>
          {r.focus.map((n) => <Note n={n} />)}
        </div>}

        {tab !== "fitness" && <span class="label">Measured: from Strava. Computed: from measured numbers and your pace model.</span>}
      </section>
      <div class="dock-wrap">{toast}</div>
    </main>
  );
}

function Note({ n }: { n: { title: string; line: string; source: string } }) {
  return (
    <div class="note-s">
      <div class="row-between"><span class="note-title">{n.title}</span><span class="label">{n.source}</span></div>
      <span class="soft">{n.line}</span>
    </div>
  );
}
